import { Alert, Box, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PageContentShell } from "../../components/layout/PageContentShell";
import { PageHeader } from "../../components/Page/PageHeader";
import { releaseNoteService, type ReleaseNote } from "../../services/productionDeploymentService";
import { apiMessage } from "../../utils/apiError";

function formatWhen(value?: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

export function ReleaseNotesPage() {
  const { t } = useTranslation();
  const [notes, setNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const items = await releaseNoteService.list();
        if (!cancelled) setNotes(items);
      } catch (e) {
        if (!cancelled) setError(apiMessage(e) || t("releaseNotes.loadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t]);

  return (
    <PageContentShell>
      <PageHeader title={t("releaseNotes.title")} subtitle={t("releaseNotes.subtitle")} />
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {loading ? (
        <CircularProgress size={28} />
      ) : notes.length === 0 ? (
        <Typography color="text.secondary">{t("releaseNotes.empty")}</Typography>
      ) : (
        <Stack spacing={2}>
          {notes.map((note) => (
            <Box key={note.id} sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "background.paper" }}>
              <Typography variant="h6">{note.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {note.version} · {formatWhen(note.publishedAt)}
                {note.publishedBy ? ` · ${note.publishedBy}` : ""}
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
                {note.gitSha ? <Chip size="small" label={note.gitSha.slice(0, 7)} /> : null}
                {(note.images || []).map((image) => (
                  <Chip key={`${image.name}-${image.tag}`} size="small" variant="outlined" label={`${image.name} ${image.tag}`} />
                ))}
              </Stack>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {note.body}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </PageContentShell>
  );
}
